
import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import AppearanceSettings from '@/components/settings/AppearanceSettings';
import DataManagement from '@/components/settings/DataManagement';
import FamilySettings from '@/components/settings/FamilySettings';
import SupportAbout from '@/components/settings/SupportAbout';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, RefreshCcw, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { logger } from '@/utils/logger';

const Settings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    logger.log('Settings page loaded, user:', user?.id);
  }, [user]);

  const handleRetry = () => {
    setLoading(true);
    setHasError(false);
    setErrorMessage(null);
    
    window.location.reload();
  };

  const handleComponentError = (error: string) => {
    logger.error('Component error:', error);
    setHasError(true);
    setErrorMessage(error);
  };

  return (
    <Layout>
      <div className="container max-w-3xl pb-16">
        <h1 className="text-2xl font-bold mb-6 text-center sm:text-left">Settings</h1>
        
        {hasError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription className="flex flex-col gap-2">
              <p>{errorMessage || 'There was an error loading the settings. This might be due to a database issue.'}</p>
              <Button 
                onClick={handleRetry} 
                variant="outline" 
                size="sm" 
                className="self-start flex items-center gap-2"
                disabled={loading}
              >
                <RefreshCcw className="h-3.5 w-3.5" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-center sm:text-left">
                Account Settings
              </CardTitle>
              <CardDescription className="text-center sm:text-left">
                Manage your name, email, and password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/account-settings">
                <Button variant="outline" className="w-full justify-between">
                  <span>Go to Account Settings</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <FamilySettings
            onError={handleComponentError}
          />

          <AppearanceSettings />

          <DataManagement />

          <SupportAbout />
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
